import i18n from 'modules/core/lib/i18n'

import { type StatusColor } from './statusColor'

// Simplified type matching what's actually fetched in the GraphQL query
type SpendingAlert = {
  id: string
  status: string
  type: string
  metadata?: { title?: string | null } | null
}

type AlertDisplay = {
  color: StatusColor
  label: string
} | null

type AlertTag = {
  text: string
  level: 'warning' | 'critical' | 'positive' | 'info'
}

function getAlertLabel(alert: SpendingAlert): string {
  if (alert.type === 'policy_compliance') {
    return (
      alert.metadata?.title || i18n.t('expenses_v2.expense.spending_alert.label.policy_compliance')
    )
  }

  switch (alert.type) {
    case 'spending_policy':
      return i18n.t('expenses_v2.expense.spending_alert.label.spending_policy')
    case 'mileage_rate_changed':
      return i18n.t('expenses_v2.expense.spending_alert.label.mileage_rate_changed')
    case 'duplicate_expense':
      return i18n.t('expenses_v2.expense.spending_alert.label.duplicate_expense')
    case 'category_conflict':
    case 'per_diem_conflict':
      return i18n.t('expenses_v2.expense.spending_alert.label.per_diem_conflict')
    default:
      return alert.type
  }
}

export function getSpendingAlertsTags(
  spendingAlertsTarget: SpendingAlert[] | null | undefined = [],
  spendingAlertsSource: SpendingAlert[] | null | undefined = []
): AlertTag[] | null {
  const allAlerts = [...(spendingAlertsTarget || []), ...(spendingAlertsSource || [])]
  const failingAlerts = allAlerts.filter(
    (alert) => alert?.status === 'failed' && alert?.type !== 'in_policy'
  )
  const inPolicyAlert = allAlerts.find((alert) => alert?.type === 'in_policy')

  if (failingAlerts.length === 0) {
    if (!inPolicyAlert) {
      return null
    }
    return [
      { text: i18n.t('expenses_v2.expense.spending_alert.label.in_policy'), level: 'positive' },
    ]
  }

  // Get unique alerts by type (keeping all policy_compliance individually)
  const uniqueAlertsByType = failingAlerts.reduce<SpendingAlert[]>((acc, alert) => {
    if (alert && !acc.some((a) => a.type === alert.type && a.type !== 'policy_compliance')) {
      acc.push(alert)
    }
    return acc
  }, [])

  return uniqueAlertsByType.map((alert) => ({
    text: getAlertLabel(alert),
    level: 'warning' as const,
  }))
}

export function getSpendingAlertsDisplay(
  spendingAlertsTarget: SpendingAlert[] | null | undefined = [],
  spendingAlertsSource: SpendingAlert[] | null | undefined = []
): AlertDisplay {
  const allAlerts = [...(spendingAlertsTarget || []), ...(spendingAlertsSource || [])]
  const failingAlerts = allAlerts.filter(
    (alert) => alert?.status === 'failed' && alert?.type !== 'in_policy'
  )
  const inPolicyAlert = allAlerts.find((alert) => alert?.type === 'in_policy')

  if (failingAlerts.length === 0) {
    if (!inPolicyAlert) {
      return null
    }
    return {
      color: 'positive',
      label: i18n.t('expenses_v2.expense.spending_alert.label.in_policy'),
    }
  }

  // Get unique alerts by type
  const uniqueAlertsByType = failingAlerts.reduce<SpendingAlert[]>((acc, alert) => {
    //we want to show all the alerts from the policy evaluation
    if (alert && !acc.some((a) => a.type === alert.type && a.type !== 'policy_compliance')) {
      acc.push(alert)
    }
    return acc
  }, [])

  // we always use warning color, out of policy alert is not blocking
  const color = 'warning'

  // Generate label
  let label: string

  if (uniqueAlertsByType.length === 1 && uniqueAlertsByType[0]) {
    label = getAlertLabel(uniqueAlertsByType[0])
  } else {
    // Multiple alerts
    label = `${uniqueAlertsByType.length} ${i18n.t(
      'expenses_v2.expense.spending_alert.label.multiple'
    )}`
  }

  return { color, label }
}
